import { Widget, Attachment } from 'graphql-common';
import { LocalWidget, GridLayoutItem } from './widgetConverters';
import { transformAttachmentsForDuplication } from './transformAttachmentForDuplication';
import { getWidgetSizeConstraints } from '../constants';

/**
 * Result of copying widgets from a template page.
 */
export interface CopyWidgetsResult {
  /** Local widgets with temporary IDs (ready for editing) */
  localWidgets: LocalWidget[];
  /** Grid layout items with matching temporary IDs */
  localGrid: GridLayoutItem[];
}

/**
 * Generates a unique temporary ID for a widget.
 * Uses timestamp + index to ensure uniqueness when copying multiple widgets.
 *
 * @param index - Index of the widget in the array (for uniqueness)
 * @returns Unique temporary ID string
 */
export function generateTempId(index: number): string {
  return `temp-${Date.now()}-${index}`;
}

/**
 * Converts a Widget from API to a LocalWidget with temporary ID for duplication.
 * Unlike `widgetToLocalWidget`, this creates a new widget without preserving the original ID.
 * Attachments are transformed for duplication so the backend creates copies.
 *
 * @param widget - Source widget from API
 * @param tempId - Temporary ID to assign to the new widget
 * @returns LocalWidget ready for local editing as a new widget
 */
export function widgetToLocalWidgetForDuplication(
  widget: Widget,
  tempId: string,
): LocalWidget {
  // Transform attachments for duplication (creates copies instead of references)
  const transformedAttachments = widget.attachmentsAttached
    ? transformAttachmentsForDuplication(
        widget.attachmentsAttached as Attachment[],
      )
    : undefined;

  return {
    tempId,
    // No 'id' - this is a new widget to be created
    kind: widget.kind,
    title: widget.title,
    description: widget.description,
    // Keep widgetTemplateId to reference the source template
    widgetTemplateId: widget.widgetTemplateId,
    fields: widget.fields as Record<string, unknown>,
    // Use transformed attachments for duplication
    attachmentsAttached:
      transformedAttachments && transformedAttachments.length > 0
        ? transformedAttachments
        : undefined,
    // Position will be determined by grid layout
    position: undefined,
    // Store original widget for reference (useful for attachment change detection)
    originalWidget: widget,
  };
}

/**
 * Creates a grid layout item from a Widget with a temporary ID.
 * Uses the widget's existing grid data if available, otherwise uses defaults.
 *
 * @param widget - Source widget containing grid data
 * @param tempId - Temporary ID to use in the grid item
 * @returns GridLayoutItem for react-grid-layout
 */
export function createGridItemFromWidget(
  widget: Widget,
  tempId: string,
): GridLayoutItem {
  const constraints = getWidgetSizeConstraints(widget.kind);

  return {
    i: tempId,
    x: widget.grid?.x ?? 0,
    y: widget.grid?.y ?? 0,
    w: widget.grid?.w ?? constraints.defaultW,
    h: widget.grid?.h ?? constraints.defaultH,
    minW: constraints.minW,
    minH: constraints.minH,
    maxW: constraints.maxW,
    maxH: constraints.maxH,
  };
}

/**
 * Copies widgets from a template page for use on another page.
 *
 * This function:
 * 1. Creates new LocalWidgets with temporary IDs (no original widget IDs)
 * 2. Transforms attachments so the backend creates copies of files
 * 3. Builds grid layout items that match the temporary IDs
 *
 * Use this when applying a template page's widgets to another page.
 * The resulting widgets will be created as new when saved.
 *
 * @param widgets - Array of widgets from the template page
 * @returns Object containing localWidgets and localGrid arrays
 *
 * @example
 * ```ts
 * const { localWidgets, localGrid } = copyWidgetsFromTemplate(defaultPageWidgets);
 * setLocalWidgets(localWidgets);
 * setLocalGrid(localGrid);
 * ```
 */
export default function copyWidgetsFromTemplate(
  widgets: Widget[],
): CopyWidgetsResult {
  if (!widgets || widgets.length === 0) {
    return {
      localWidgets: [],
      localGrid: [],
    };
  }

  const baseTimestamp = Date.now();
  const localWidgets: LocalWidget[] = [];
  const localGrid: GridLayoutItem[] = [];

  widgets.forEach((widget, index) => {
    // Generate unique tempId using timestamp + index
    const tempId = `temp-${baseTimestamp}-${index}`;

    // Convert widget to local format with tempId and transformed attachments
    const localWidget = widgetToLocalWidgetForDuplication(widget, tempId);
    localWidgets.push(localWidget);

    // Create corresponding grid item with the same tempId
    const gridItem = createGridItemFromWidget(widget, tempId);
    localGrid.push(gridItem);
  });

  return {
    localWidgets,
    localGrid,
  };
}
